import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'

// Layout components
import Navbar from './components/layout/Navbar'
import Footer from './components/layout/Footer'
import AdminLayout from './components/AdminLayout'

// Customer Page components
import HomePage from './pages/HomePage'
import ProductsPage from './pages/ProductsPage'
import ProductDetailPage from './pages/ProductDetailPage'
import CartPage from './pages/CartPage'
import CheckoutPage from './pages/CheckoutPage'
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import MyOrdersPage from './pages/MyOrdersPage'
import PaymentMethodsPage from './pages/PaymentMethodsPage'
import ServicesPage from './pages/ServicesPage'
import BlogsPage from './pages/BlogsPage'
import SolarPricingPage from './pages/SolarPricingPage'
import AboutPage from './pages/AboutPage'
import ContactPage from './pages/ContactPage'

// Admin Page components
import AdminLoginPage from './pages/admin/LoginPage'
import AdminDashboard from './pages/admin/Dashboard'
import AdminProductsPage from './pages/admin/ProductsPage'
import AdminOrdersPage from './pages/admin/OrdersPage'
import AdminUsersPage from './pages/admin/UsersPage'
import AdminRidersPage from './pages/admin/RidersPage'
import AdminPaymentProofsPage from './pages/admin/PaymentProofsPage'
import AdminBlogsPage from './pages/admin/BlogsPage'
import AdminServicesPage from './pages/admin/ServicesPage'
import AdminServiceInquiriesPage from './pages/admin/ServiceInquiriesPage'
import AdminSalesPage from './pages/admin/sales/SalesPage'
import AdminNewSalePage from './pages/admin/sales/NewSalePage'
import AdminPurchasesPage from './pages/admin/purchases/PurchasesPage'
import AdminNewPurchasePage from './pages/admin/purchases/NewPurchasePage'
import AdminExpensesPage from './pages/admin/expenses/ExpensesPage'
import AdminPartnersPage from './pages/admin/partners/PartnersPage'
import AdminFinancePage from './pages/admin/finance/FinanceDashboard'
import AdminSeedDatabasePage from './pages/admin/SeedDatabasePage'

// Rider Page components
import RiderLoginPage from './pages/rider/LoginPage'
import RiderDashboardPage from './pages/rider/DashboardPage'
import RiderDeliveryDetailPage from './pages/rider/DeliveryDetailPage'

// Protected route wrapper
import ProtectedRoute from './components/auth/ProtectedRoute'
import { useAuth } from './contexts/AuthContext'

// Admin route protection
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth()

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!currentUser) {
    return <Navigate to="/admin/login" replace />
  }

  if (currentUser.role !== 'admin') {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access the admin portal.</p>
      </Box>
    )
  }

  return <>{children}</>
}

// Rider route protection
function RiderRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth()

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!currentUser) {
    return <Navigate to="/rider/login" replace />
  }

  if (currentUser.role !== 'delivery_boy') {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>This portal is only for delivery riders.</p>
      </Box>
    )
  }

  return <>{children}</>
}

// Layout wrapper that conditionally shows Navbar/Footer
function AppLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const isAdminRoute = location.pathname.startsWith('/admin')
  const isRiderRoute = location.pathname.startsWith('/rider')
  const hideLayout = isAdminRoute || isRiderRoute

  if (hideLayout) {
    return <>{children}</>
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>
      <Footer />
    </Box>
  )
}

function App() {
  return (
    <AppLayout>
      <Routes>
        {/* ============ PUBLIC CUSTOMER ROUTES ============ */}
        <Route path="/" element={<HomePage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/products/:id" element={<ProductDetailPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/blogs" element={<BlogsPage />} />
        <Route path="/solar-pricing" element={<SolarPricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/payment-methods" element={<PaymentMethodsPage />} />

        {/* Auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Protected user routes */}
        <Route path="/my-orders" element={
          <ProtectedRoute>
            <MyOrdersPage />
          </ProtectedRoute>
        } />

        {/* ============ ADMIN ROUTES ============ */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="sales" element={<AdminSalesPage />} />
          <Route path="sales/new" element={<AdminNewSalePage />} />
          <Route path="purchases" element={<AdminPurchasesPage />} />
          <Route path="purchases/new" element={<AdminNewPurchasePage />} />
          <Route path="expenses" element={<AdminExpensesPage />} />
          <Route path="partners" element={<AdminPartnersPage />} />
          <Route path="finance" element={<AdminFinancePage />} />
          <Route path="products" element={<AdminProductsPage />} />
          <Route path="orders" element={<AdminOrdersPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="riders" element={<AdminRidersPage />} />
          <Route path="payment-proofs" element={<AdminPaymentProofsPage />} />
          <Route path="blogs" element={<AdminBlogsPage />} />
          <Route path="services" element={<AdminServicesPage />} />
          <Route path="service-inquiries" element={<AdminServiceInquiriesPage />} />
          <Route path="seed" element={<AdminSeedDatabasePage />} />
        </Route>

        {/* ============ RIDER ROUTES ============ */}
        <Route path="/rider/login" element={<RiderLoginPage />} />
        <Route path="/rider" element={
          <RiderRoute>
            <RiderDashboardPage />
          </RiderRoute>
        } />
        <Route path="/rider/delivery/:orderId" element={
          <RiderRoute>
            <RiderDeliveryDetailPage />
          </RiderRoute>
        } />
      </Routes>
    </AppLayout>
  )
}

export default App